import type { RecaptchaVerifier } from 'firebase/auth';

import { doc, setDoc, getDoc, collection } from 'firebase/firestore';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import {
  PhoneAuthProvider,
  signOut as _signOut,
  signInWithCredential,
  signInWithPopup as _signInWithPopup,
  GoogleAuthProvider as _GoogleAuthProvider,
  GithubAuthProvider as _GithubAuthProvider,
  TwitterAuthProvider as _TwitterAuthProvider,
  sendEmailVerification as _sendEmailVerification,
  signInWithPhoneNumber as _signInWithPhoneNumber,
  sendPasswordResetEmail as _sendPasswordResetEmail,
  signInWithEmailAndPassword as _signInWithEmailAndPassword,
  createUserWithEmailAndPassword as _createUserWithEmailAndPassword,
} from 'firebase/auth';

import { isNativePlatform } from 'src/utils/capacitor';

import { AUTH, FIRESTORE } from 'src/lib/firebase';
import { getStoredFCMToken, deactivateDeviceToken } from 'src/actions/fcm-token';

// ----------------------------------------------------------------------

export type SignInParams = {
  email: string;
  password: string;
};

export type SignUpParams = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
};

export type ForgotPasswordParams = {
  email: string;
};

export type SignInWithPhoneParams = {
  phoneNumber: string;
  appVerifier: RecaptchaVerifier;
};

export type VerifyPhoneCodeParams = {
  verificationId: string;
  code: string;
};

/** **************************************
 * Sign in
 *************************************** */
export const signInWithPassword = async ({ email, password }: SignInParams): Promise<void> => {
  try {
    await _signInWithEmailAndPassword(AUTH, email, password);

    const user = AUTH.currentUser;

    if (!user?.emailVerified) {
      throw new Error('Email not verified!');
    }
  } catch (error) {
    console.error('Error during sign in with password:', error);
    throw error;
  }
};

export const signInWithGoogle = async (): Promise<void> => {
  const provider = new _GoogleAuthProvider();
  await _signInWithPopup(AUTH, provider);
};

export const signInWithGithub = async (): Promise<void> => {
  const provider = new _GithubAuthProvider();
  await _signInWithPopup(AUTH, provider);
};

export const signInWithTwitter = async (): Promise<void> => {
  const provider = new _TwitterAuthProvider();
  await _signInWithPopup(AUTH, provider);
};

/** **************************************
 * Sign up
 *************************************** */
export const signUp = async ({
  email,
  password,
  firstName,
  lastName,
}: SignUpParams): Promise<void> => {
  try {
    const newUser = await _createUserWithEmailAndPassword(AUTH, email, password);

    /*
     * (1) If skip emailVerified
     * Remove : await _sendEmailVerification(newUser.user);
     */
    await _sendEmailVerification(newUser.user);

    const userProfile = doc(collection(FIRESTORE, 'users'), newUser.user?.uid);

    await setDoc(userProfile, {
      uid: newUser.user?.uid,
      email,
      displayName: `${firstName} ${lastName}`,
    });
  } catch (error) {
    console.error('Error during sign up:', error);
    throw error;
  }
};

/** **************************************
 * Sign out
 *************************************** */
export const signOut = async (): Promise<void> => {
  try {
    // Deactivate FCM token before signing out
    const storedToken = getStoredFCMToken();
    if (storedToken) {
      await deactivateDeviceToken(storedToken).catch(() => {});
    }

    // Native sign out
    if (isNativePlatform()) {
      await FirebaseAuthentication.signOut().catch(() => {});
    }

    // Web SDK sign out with timeout (prevents hanging on native where web SDK may have no user)
    await Promise.race([
      _signOut(AUTH),
      new Promise((resolve) => {
        setTimeout(resolve, 2000);
      }),
    ]);

    // Clear all stored auth data
    if (typeof window !== 'undefined') {
      localStorage.clear();
      sessionStorage.clear();
    }
  } catch (error) {
    console.error('Error during sign out:', error);
    // Still clear storage even if sign out fails
    if (typeof window !== 'undefined') {
      localStorage.clear();
      sessionStorage.clear();
    }
  }
};

/** **************************************
 * Reset password
 *************************************** */
export const sendPasswordResetEmail = async ({ email }: ForgotPasswordParams): Promise<void> => {
  await _sendPasswordResetEmail(AUTH, email);
};

/** **************************************
 * Phone authentication
 *************************************** */
export const signInWithPhoneNumber = async ({
  phoneNumber,
  appVerifier,
}: SignInWithPhoneParams): Promise<string> => {
  try {
    const confirmationResult = await _signInWithPhoneNumber(AUTH, phoneNumber, appVerifier);
    return confirmationResult.verificationId;
  } catch (error) {
    console.error('Error during phone number sign in:', error);
    throw error;
  }
};

export const verifyPhoneCode = async ({
  verificationId,
  code,
}: VerifyPhoneCodeParams): Promise<void> => {
  try {
    const credential = PhoneAuthProvider.credential(verificationId, code);
    await signInWithCredential(AUTH, credential);

    const user = AUTH.currentUser;

    if (user) {
      // Check if user profile exists in Firestore
      const userRef = doc(collection(FIRESTORE, 'users'), user.uid);
      const userSnap = await getDoc(userRef);

      // Create user profile if it doesn't exist (sign up flow)
      if (!userSnap.exists()) {
        await setDoc(userRef, {
          uid: user.uid,
          phoneNumber: user.phoneNumber,
          displayName: user.displayName || 'User',
          createdAt: new Date().toISOString(),
        });
      }
    }
  } catch (error) {
    console.error('Error during phone code verification:', error);
    throw error;
  }
};

/** **************************************
 * Native phone authentication (Capacitor)
 *************************************** */

type NativePhoneResult = {
  autoVerified: boolean;
  verificationId?: string;
  credential?: any;
};

export const nativeSignInWithPhoneNumber = async (
  phoneNumber: string
): Promise<NativePhoneResult> =>
  new Promise((resolve, reject) => {
    // eslint-disable-next-line prefer-const
    let codeSentHandle: any;
    // eslint-disable-next-line prefer-const
    let failedHandle: any;
    // eslint-disable-next-line prefer-const
    let completedHandle: any;

    const cleanup = () => {
      clearTimeout(timeoutId);
      // Handles may be Promises (Capacitor 7+) or direct objects
      const removeListener = (handle: any) => {
        if (!handle) return;
        if (typeof handle.remove === 'function') {
          handle.remove();
        } else if (handle instanceof Promise) {
          handle.then((h: any) => h?.remove?.()).catch(() => {});
        }
      };
      removeListener(codeSentHandle);
      removeListener(failedHandle);
      removeListener(completedHandle);
    };

    // Timeout after 60 seconds
    const timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error('Phone verification timed out. Please try again.'));
    }, 60000);

    // Listen for verification code sent (normal flow)
    codeSentHandle = FirebaseAuthentication.addListener('phoneCodeSent', (event: any) => {
      cleanup();
      resolve({
        autoVerified: false,
        verificationId: event.verificationId,
      });
    });

    // Listen for verification failed
    failedHandle = FirebaseAuthentication.addListener('phoneVerificationFailed', (event: any) => {
      cleanup();
      reject(new Error(event?.message || 'Phone verification failed'));
    });

    // Listen for auto-verification (Android)
    completedHandle = FirebaseAuthentication.addListener(
      'phoneVerificationCompleted',
      (event: any) => {
        cleanup();
        resolve({
          autoVerified: true,
          credential: event?.credential,
        });
      }
    );

    // Small delay to ensure listeners are registered before calling native plugin
    setTimeout(() => {
      FirebaseAuthentication.signInWithPhoneNumber({ phoneNumber }).catch((error: any) => {
        cleanup();
        reject(error);
      });
    }, 100);
  });

export const nativeConfirmPhoneNumber = async (
  verificationId: string,
  verificationCode: string
) => {
  const result = await FirebaseAuthentication.confirmVerificationCode({
    verificationId,
    verificationCode,
  });
  return result;
};

export const nativeGetCurrentUser = async () => {
  const result = await FirebaseAuthentication.getCurrentUser();
  return result.user;
};

export const nativeGetIdToken = async (forceRefresh = false) => {
  const result = await FirebaseAuthentication.getIdToken(
    forceRefresh ? { forceRefresh: true } : undefined
  );
  return result.token;
};
